import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  useToast,
  VStack
} from "@chakra-ui/react";
import { PiDeviceMobileDuotone as FiSmartphone } from "react-icons/pi";
import QRCode from "qrcode.react";
import { twoFactorTotpSetup, twoFactorTotpEnable, twoFactorTotpDisable } from "@lubbezposrednio-frontend/core";
import { ProfileViewModel } from "../ProfileViewModel";
import { SecurityCard } from "./SecurityCard";

const ACCENT = "#0E7A9E";

/**
 * Trzecia metoda 2FA: kod z aplikacji authenticator (TOTP). Enrollment przez QR + ręczny klucz,
 * aktywacja pierwszym kodem. Po włączeniu logowanie domyślnie prosi o kod z aplikacji.
 */
export const TotpCard: React.FC<{ model: ProfileViewModel }> = observer(({ model }) => {
  const toast = useToast();
  const [setup, setSetup] = useState<{ uri: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await twoFactorTotpSetup();
      setSetup({ uri: res.data.provisioning_uri, secret: res.data.secret });
    } catch {
      toast({ title: "Nie udało się rozpocząć konfiguracji.", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (value: string) => {
    setBusy(true);
    try {
      await twoFactorTotpEnable(value);
      model.setTotpEnabled(true);
      setSetup(null);
      setCode("");
      toast({ title: "Aplikacja authenticator aktywna ✓", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nieprawidłowy kod — spróbuj ponownie.", status: "error", isClosable: true });
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await twoFactorTotpDisable();
      model.setTotpEnabled(false);
      toast({ title: "Aplikacja authenticator wyłączona.", status: "info", isClosable: true });
    } catch {
      toast({ title: "Nie udało się wyłączyć.", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SecurityCard
      icon={FiSmartphone}
      accent={ACCENT}
      full
      title="Aplikacja authenticator (kod)"
      subtitle="Trzecia metoda 2FA — niezależna od telefonu z apką i od e-maila. Generuje 6-cyfrowy kod offline (Google Authenticator, 1Password, Authy)."
      badge={model.totpEnabled ? <Badge colorScheme="green" borderRadius="6px">Aktywna</Badge> : undefined}
      action={
        busy ? (
          <Spinner size="sm" color={ACCENT} />
        ) : model.totpEnabled ? (
          <Button variant="link" color="gray.400" fontWeight="600" fontSize="14px" onClick={disable}>
            Wyłącz
          </Button>
        ) : undefined
      }
    >
      {!model.totpEnabled && !setup && (
        <Button leftIcon={<FiSmartphone />} variant="outline" borderColor={ACCENT} color={ACCENT} borderRadius="12px" fontWeight="700" isLoading={busy} onClick={startSetup} _hover={{ bg: "rgba(14,122,158,0.06)" }}>
          Skonfiguruj
        </Button>
      )}

      {setup && (
        <Flex direction={{ base: "column", md: "row" }} gap="22px" align={{ md: "center" }}>
          <Box bg="white" border="1px solid rgba(67,0,152,0.12)" borderRadius="16px" p="14px" alignSelf={{ base: "center", md: "flex-start" }}>
            <QRCode value={setup.uri} size={150} fgColor="#430098" />
          </Box>
          <VStack align="stretch" spacing="12px" flex="1">
            <Text fontSize="13px" color="gray.500">
              Zeskanuj kod aplikacją authenticator, a potem wpisz wygenerowany 6-cyfrowy kod. Nie możesz zeskanować?
              Wpisz ręcznie klucz: <Text as="span" fontFamily="mono" color="primary" wordBreak="break-all">{setup.secret}</Text>
            </Text>
            <HStack justify="space-between">
              <PinInput otp type="number" value={code} onChange={setCode} onComplete={confirm} size="lg" focusBorderColor="primary">
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <PinInputField key={i} flex="1" fontWeight="800" />
                ))}
              </PinInput>
            </HStack>
            <Button bg="primary" color="white" borderRadius="12px" fontWeight="700" isLoading={busy} isDisabled={code.length < 6} onClick={() => confirm(code)} _hover={{ bg: "#360078" }}>
              Aktywuj
            </Button>
          </VStack>
        </Flex>
      )}
    </SecurityCard>
  );
});
